import './index.less';
import '@/declare/styles/tn-menu.less';
import { useContext, useEffect, useRef, useState } from 'react';
import { MajorMenu, NavFirstLevelButton } from 'bimui';
import { EngineContext, NavigationContext, RegisterContext } from '@/App';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { addFile, findFileByProjectUuid } from '@/declare/api/project/file';
import { FileType } from '@/declare/enums/file-type';
import { File } from "@/declare/interface/project/file"
import { BaseUtils } from "tnbase";
import localStore from '@/store/local-store';
import { TabItemProps } from '@/store/navigation-context';
import { emitter } from '@/utils/mitt';
import { Emit_Event_Common_Tool } from '@/declare/enums/emit-event';
import { SIDEBAR_TYPE, TOOL_TYPE } from '@/declare/enums/menu-type';
import { message } from 'antd';
import { Major } from '@/declare/interface/project/major';
import { coreAxios } from '@/declare/api/axios';


interface Iprops {
    onClick?: (type) => void
}

/**
 * 动态工具区的布局
 * 实现多个功能模块的切换
 * 每个模块对应一个子菜单组件
 */
export default function MenuBar(props: Iprops) {
    const { projectUuid } = useParams();
    const registerContext = useContext(RegisterContext);
    const engineContext = useContext(EngineContext); // 获取上下文
    const navigateContext = useContext(NavigationContext); // 获取上下文
    const navigate = useNavigate();
    const location = useLocation();
    // 注册存储
    const [sidebarPanelInitStatus, setSidebarPanelInitStatus] = useState<{}>({});

    /**
     * 引用底部容器以实现滚动操作
     */
    const scrollRef = useRef(null);
    /**
     * 当前激活的专业模块
     * - 初始值为 `MENU_MODULE.COMMON`，表示通用模块。
     */
    const [activeMajor, setActiveMajor] = useState<MajorMenu>();

    const activeMajorRef = useRef(activeMajor);


    useEffect(() => {
        activeMajorRef.current = activeMajor;
    }, [activeMajor])

    /**
     * 拿到面板的缓存状态
     */
    useEffect(() => {
        if (registerContext.currentActivedSidebarKey == null) return;
        const status = localStore.getSidebarStatus(registerContext.currentActivedSidebarKey);
        setSidebarPanelInitStatus(status);
    }, [registerContext.currentActivedSidebarKey])

    // 根据页签路由找到当前激活的侧边栏
    useEffect(() => {
        const sidebarTabs = localStore.sidebarTabs;
        if (sidebarTabs == null) return;
        const keys = Object.keys(sidebarTabs);
        if (keys.length === 0) return;
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = sidebarTabs[key];
            if (value == null || value.activeTabKey == null) continue;
            if (location.pathname.includes(value.activeTabKey)) {
                // 当前的key就是当前激活的侧边栏key
                registerContext.activeSidebar(key);
                return;
            }
        }
    }, [])

    useEffect(() => {
        let majorUuid = localStore.activeMajor
        if (!majorUuid) {
            majorUuid = "project";
        }
        let menu = registerContext.menus.find(item => item.pluginUuid == majorUuid);
        if (!menu) {
            if (registerContext.menus.length > 0) {
                setActiveMajor(registerContext.menus[0]);
            }
        } else {
            console.log('menu', menu)
            setActiveMajor({ ...menu });
        }
    }, [registerContext.menus])

    useEffect(() => {
        emitter.on(Emit_Event_Common_Tool.ACTIVE_MAJOR, handleActiveMajor);

        return () => {
            emitter.off(Emit_Event_Common_Tool.ACTIVE_MAJOR, handleActiveMajor);
        };
    }, [activeMajor])

    const handleActiveMajor = (value: string) => {
        // 传过来的是majorUuid
        if (activeMajorRef.current?.pluginUuid == value) return;
        let menu = registerContext.menus.find(item => item.pluginUuid == value);
        if (!menu) return;
        setActiveMajor(menu);
        localStore.activeMajor = value;
        // 激活分组的侧边栏
        const activeSidebarKey = SIDEBAR_TYPE.MODEL;
        const activeToolKey = localStore.getToolKeyByMajor(value);
        const tabs = localStore.getTabsBySidebarKey(activeSidebarKey);
        registerContext.activeSidebar(activeSidebarKey);
        registerContext.activeTool(activeToolKey);
        navigateContext.setTabs(tabs);
    }

    /**
     * 初始化
     */
    useEffect(() => {
        if (registerContext.menus.length <= 0) return;
        if (activeMajor != null) return;
        let activeMajorType = localStore.activeMajor;
        if (activeMajorType != null) {
            let menu = registerContext.menus.find(item => item.pluginUuid == activeMajorType);
            setActiveMajor(menu);
        } else {
            let menu = registerContext.menus[0];
            setActiveMajor(menu);
            localStore.activeMajor = menu.type;
        }
    }, [registerContext.menus])

    /**
     * 处理滚轮滚动事件
     * - 滚动菜单栏内容。
     * 
     * @param event 水平滚动事件对象
     */
    const handleWheel = (event) => {
        event.preventDefault();
        const delta = event.deltaY || event.detail || event.wheelDelta;
        if (scrollRef.current) {
            scrollRef.current.scrollLeft += delta;
        }
    };
    /**
     * 注册和移除滚轮滚动事件监听器
     * - 在组件挂载时注册监听器。
     * - 在组件卸载时移除监听器。
     */
    useEffect(() => {
        const divElement = scrollRef.current;
        if (divElement) {
            divElement.addEventListener('wheel', handleWheel);
        }

        return () => {
            if (divElement) {
                divElement.removeEventListener('wheel', handleWheel);
            }
        };
    }, [handleWheel]);

    /**
     * 切换专业
     * 恢复上次打开的侧边栏、工具面板、页签
     */
    const handleChangeMajor = (majorUuid: string) => {
        localStore.activeMajor = majorUuid;
        const activeSidebarKey = localStore.getSidebarKeyByMajor(majorUuid);
        const activeToolKey = localStore.getToolKeyByMajor(majorUuid);
        const tabs = localStore.getTabsBySidebarKey(activeSidebarKey);
        const activeTabKey = localStore.getActiveTabKeyBySidebarKey(activeSidebarKey);
        registerContext.activeSidebar(activeSidebarKey);
        registerContext.activeTool(activeToolKey);
        navigateContext.setTabs(tabs);
        if (activeTabKey != null) {
            navigate(activeTabKey);
            navigateContext.setActiveTabKey(activeTabKey);
        } else {
            navigate(`/projects/${projectUuid}`)
        }
    }

    return (
        <div className='project-menu-container'>
            <div className='menu-bar'>
                {registerContext.menus.map((menu, idx) => (
                    <NavFirstLevelButton active={activeMajor?.pluginUuid == menu.pluginUuid} onClick={() => {
                        if (activeMajor?.pluginUuid == menu.pluginUuid) return;
                        setActiveMajor(menu);
                        handleChangeMajor(menu.pluginUuid);
                    }}>
                        {menu.title}
                    </NavFirstLevelButton>
                ))}
            </div>
            <div className='menu-content'>
                {!!activeMajor && (
                    <activeMajor.menuItem {...engineContext}
                        axios={coreAxios}
                        emitter={emitter}
                        emitterType={Emit_Event_Common_Tool}
                        currentActivedSidebarKey={registerContext.currentActivedSidebarKey}
                        currentActivedToolKey={registerContext.currentActivedToolKey}
                        projectUuid={projectUuid}
                        navigate={(key) => {
                            navigate(key)
                        }}
                        panelInitStatus={sidebarPanelInitStatus}
                        onStatusChange={(status) => {
                            localStore.setSidebarStatus(registerContext.currentActivedSidebarKey, status);
                        }}
                        activeSidebarPanel={(key: string) => {
                            registerContext.activeSidebar(key);
                        }} activeToolPanel={(key: string) => {
                            registerContext.activeTool(key);
                        }} coreSidebarType={SIDEBAR_TYPE} coreToolType={TOOL_TYPE} />
                )}

            </div>

        </div>
    )
}

{/* <div className='dynamic-function-container'>
<div className='top-container'>
    {MENU_MODULE_LIST?.map((item, idx) => (
        <div key={idx}
            className={classNames({
                'major-block': true,
                'major-block-active': item.value === activeMajor
            })}
            onClick={() => setActiveMajor(item.value)}
        >{item.label}</div>
    ))}
</div>
<div className='bottom-container' ref={scrollRef}>
    {activeMajor === MENU_MODULE.PROJECT && <ProjectMenu />}
    {activeMajor === MENU_MODULE.COMMON && <CommonMenu />}
    {activeMajor === MENU_MODULE.PLANNING && <PlanningMenu />}
    {activeMajor === MENU_MODULE.BUILDING && <BuildingMenu />}
    {activeMajor === MENU_MODULE.STRUCTURE && <StructureMenu />}
    {activeMajor === MENU_MODULE.HAVC && <HavcMenu />}
    {activeMajor === MENU_MODULE.WATER && <WaterMenu />}
    {activeMajor === MENU_MODULE.ELECTRICITY && <ElectricityMenu />}
    {activeMajor === MENU_MODULE.FIRE && <FireMenu />}
    {activeMajor === MENU_MODULE.MAINT && <MaintMenu />}
</div>
</div> */}